import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Paper,
  Divider,
  Toolbar,
  IconButton,
  CircularProgress
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SettingsIcon from "@mui/icons-material/Settings";
import { useDispatch } from "react-redux";
import { messageShow } from "../common/api/actions";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../setup/config/env";
import SectionPaper from "../common/SectionPaper";
import { checkPermission } from "../common/Sidebar/checkPermission";

const NewsletterSettings = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const [isNewsletterOtpEnabled, setIsNewsletterOtpEnabled] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [permissions, setPermissions] = useState({ canView: false, canEdit: false, canCreate: false, canDelete: false });

    useEffect(() => {
        const fetchSettingsAndPerms = async () => {
            try {
                const perms = await checkPermission("marketing", "newsletter");
                setPermissions(perms);

                const response = await axios.get(`${API_URL}/api/messaging/settings`);
                if (response.data) {
                    setIsNewsletterOtpEnabled(response.data.isNewsletterOtpEnabled || false);
                }
                setIsLoading(false);
            } catch (err) {
                console.error("Error loading newsletter settings:", err);
                dispatch(messageShow("Failed to load newsletter settings"));
                setIsLoading(false);
            }
        };
        fetchSettingsAndPerms();
    }, [dispatch]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // We only want to update the OTP toggle in Setting collection
            // The API saveOrUpdateEmailSettings updated to handle this but we can use a more targeted one if preferred
            // For now, we'll use the existing one but only send the toggles
            const response = await axios.put(`${API_URL}/api/messaging/update-settings`, {
                isNewsletterOtpEnabled
                // These will be ignored by the backend if we don't send them or if we use the split logic correctly
                // Actually our backend saveOrUpdateEmailSettings currently REQUIRES other fields.
                // I should probably create a specific API for global settings or make fields optional.
            });
            
            if (response.data) {
                dispatch(messageShow("Newsletter settings saved successfully!"));
            }
        } catch (err) {
            console.error("Error saving newsletter settings:", err);
            dispatch(messageShow("Error saving settings"));
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4 }}>
            <Toolbar sx={{ px: '0 !important', mb: 3 }}>
                <Box sx={{ flexGrow: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton onClick={() => history.goBack()} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0d3559' }}>
                            Newsletter Settings
                        </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: '#8898AA', ml: 5 }}>
                        Configure how users interact with your newsletter.
                    </Typography>
                </Box>
            </Toolbar>

            <SectionPaper>
                <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#0d3559' }}>
                        Subscription Verification
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#525F7F', mb: 3 }}>
                        By enabling OTP verification, users will receive a 6-digit code to their email that they must enter to confirm their subscription. This helps prevent spam and ensures high-quality leads.
                    </Typography>
                    
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isNewsletterOtpEnabled}
                                onChange={(e) => setIsNewsletterOtpEnabled(e.target.checked)}
                                disabled={!permissions.canEdit}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#0d3559',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#0d3559',
                                    },
                                }}
                            />
                        }
                        label={
                            <Typography sx={{ fontWeight: 600, color: '#0d3559' }}>
                                Enable OTP Verification
                            </Typography>
                        }
                    />

                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button
                            variant="contained"
                            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            onClick={handleSave}
                            disabled={isSaving || !permissions.canEdit}
                            sx={{ 
                                bgcolor: permissions.canEdit ? '#0d3559' : '#e0e0e0', 
                                color: permissions.canEdit ? 'white' : '#9e9e9e', 
                                px: 4, 
                                py: 1,
                                borderRadius: '8px',
                                '&:hover': { bgcolor: '#092a46' } 
                            }}
                        >
                            {isSaving ? "Saving..." : "Save Changes"}
                        </Button>
                    </Stack>
                </Box>
            </SectionPaper>
        </Box>
    );
};

export default NewsletterSettings;
